//Contact info update requests (mobile/email changes made by the customer through
//PACI + OTP).  The customer doc is already updated when these are created; pending
//rows are the ops queue for manually entering the change on the trading system.
//Both views are Typesense-backed (update_requests collection, synced by the
//onChangeUpdateRequest trigger), same flow as orders/services/transactions.

import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { Alert, Button, Checkbox, Group, Modal, Stack, Text, TextInput, Title } from "@mantine/core";

import { useFunctions } from "../../helpers/firebaseContext";
import { CustomerUpdateField, SendInfoUpdateLinkRequest, SendInfoUpdateLinkResult } from "../../types";
import UpdateRequestsInstantSearch from "../../components/update_requests_instant_search";

export const CustomerUpdateRequests = ({ customerID }: { customerID: string }) => {
    return (
        <UpdateRequestsInstantSearch
            indexName={import.meta.env.VITE_TYPESENSE_UPDATE_REQUESTS_COLLECTION!}
            defaultFilter={`customerID:=${customerID}`}
            showCustomer={false}
        />
    );
};

//Modal for CR to send an info_update link to an arbitrary phone number.  The
//customer is discovered after PACI, so no customer selection is needed.
const SendUpdateLinkModal = ({ opened, onClose }: { opened: boolean, onClose: () => void }) => {
    const functions = useFunctions();

    const [mobile, setMobile] = useState("");
    const [civilID, setCivilID] = useState("");
    const [fields, setFields] = useState<string[]>(["mobile", "email"]);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<{ ok: boolean, message: string } | null>(null);

    const send = async () => {
        setSending(true);
        setResult(null);
        try {
            const sendLink = httpsCallable<SendInfoUpdateLinkRequest, SendInfoUpdateLinkResult>(functions, "sendInfoUpdateLink");
            const response = await sendLink({
                mobile: mobile.trim(),
                updatable_fields: fields as CustomerUpdateField[],
                ...(civilID.trim() ? { civilID: civilID.trim() } : {}),
            });
            if (response.data.status === "success") {
                setResult({ ok: true, message: `Link ${response.data.linkID} sent to ${mobile.trim()} on WhatsApp.` });
                setMobile("");
                setCivilID("");
            } else {
                setResult({ ok: false, message: response.data.message || "Could not send the link" });
            }
        } catch (e: any) {
            setResult({ ok: false, message: e?.message || "Could not send the link" });
        }
        setSending(false);
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Send Update Link">
            <Stack>
                <Text size="sm" c="dimmed">
                    Sends a WhatsApp link the client can use to update their details after
                    PACI authentication. The number does not have to match any customer
                    record — the client is identified by PACI.
                </Text>
                <TextInput
                    label="Mobile number"
                    placeholder="9xxxxxxx"
                    required
                    value={mobile}
                    onChange={(e) => setMobile(e.currentTarget.value)}
                />
                <Checkbox.Group
                    label="What can the client change?"
                    value={fields}
                    onChange={setFields}
                >
                    <Group mt="xs">
                        <Checkbox value="mobile" label="Mobile number" />
                        <Checkbox value="email" label="Email" />
                        <Checkbox value="iban" label="Bank account" />
                    </Group>
                </Checkbox.Group>
                <TextInput
                    label="Civil ID (optional)"
                    description="Lock the link to a known client — PACI must then match this civil ID"
                    value={civilID}
                    onChange={(e) => setCivilID(e.currentTarget.value)}
                />
                {result && (
                    <Alert color={result.ok ? "green" : "red"}>{result.message}</Alert>
                )}
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>Close</Button>
                    <Button loading={sending} disabled={!mobile.trim() || fields.length === 0} onClick={send}>
                        Send on WhatsApp
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

//Ops queue of update requests across all customers (defaults to pending)
export const UpdateRequestsQueue = () => {
    const [sendModalOpened, setSendModalOpened] = useState(false);

    return (
        <>
            <UpdateRequestsInstantSearch
                indexName={import.meta.env.VITE_TYPESENSE_UPDATE_REQUESTS_COLLECTION!}
                topElements={
                    <div>
                        <Title order={2}>Info Updates</Title>
                        <Text c="dimmed" size="sm">
                            Changes already applied in the CRM that still need to be entered manually on the trading system.
                        </Text>
                        <Button mt="xs" onClick={() => setSendModalOpened(true)}>Send Update Link</Button>
                    </div>
                }
            />
            <SendUpdateLinkModal opened={sendModalOpened} onClose={() => setSendModalOpened(false)} />
        </>
    );
};

export default CustomerUpdateRequests;
